import { z } from "zod";
import { CalorieTargetSchema, CookingSkillSchema, GoalTypeSchema, HardConstraintSchema } from "./profile.js";
import { CanonicalUnitSchema, EntitlementSummarySchema, IsoDateTimeSchema, LocaleSchema } from "./primitives.js";
import { MealOutcomeRequestBaseSchema, MealOutcomeResponseSchema, partialRequiresActualServings, } from "./meals.js";
import { PantryConfirmationRequestSchema, PantryExtractionRequestSchema, PantryExtractionResponseSchema, PantryResponseSchema, } from "./pantry.js";
import { PlanApplyRequestSchema, PlanMutationResultSchema, PlanProposalRequestSchema, PlanProposalResponseSchema, } from "./plans.js";
import { MemoryCandidateActionResponseSchema, MemoryCandidateCreateRequestSchema, MemoryCandidateSchema, MemoryChoiceSchema, } from "./memory.js";
import { InsightsResponseSchema } from "./insights.js";
import { ProgressSummaryResponseSchema } from "./progress.js";
/**
 * Amy Gateway tool I/O schemas — the MVP tool subset from
 * instructions.md T14 / api-contracts.md "Amy Gateway tools". Every tool
 * Lambda derives the end user exclusively from validated JWT claims
 * propagated by the platform (ADR-012); no tool input schema below
 * accepts a user/household identifier as an argument. Tools call the
 * *same* Lambdas as the REST API in `endpoints.ts` — no parallel logic —
 * so several tool schemas intentionally reuse or lightly extend the
 * corresponding endpoint request/response shapes.
 */
const EmptyInputSchema = z.object({}).strict();
// ---------------------------------------------------------------------------
// get_user_context — minimum authorized per-turn context pack
// ---------------------------------------------------------------------------
export const RecentOutcomeSummarySchema = z.object({
    mealId: z.string(),
    recipeId: z.string(),
    outcome: z.enum(["had", "partial", "skipped", "changed"]),
    recordedAt: IsoDateTimeSchema,
});
export const RelevantMemorySummarySchema = z.object({
    id: z.string(),
    candidateType: z.string(),
    candidateValue: z.string(),
    effectDescription: z.string(),
});
export const UserContextPackSchema = z.object({
    hardConstraints: z.array(HardConstraintSchema),
    goal: z.object({ goalType: GoalTypeSchema, calorieTarget: CalorieTargetSchema }),
    currentPlan: z.object({ planId: z.string(), revision: z.number().int() }).nullable(),
    pantrySummary: z.object({
        itemCount: z.number().int().nonnegative(),
        uncertainItemCount: z.number().int().nonnegative(),
    }),
    recentOutcomes: z.array(RecentOutcomeSummarySchema),
    relevantMemories: z.array(RelevantMemorySummarySchema),
    entitlement: EntitlementSummarySchema,
});
// ---------------------------------------------------------------------------
// search_recipes — sourced catalog results only
// ---------------------------------------------------------------------------
export const SearchRecipesInputSchema = z.object({
    query: z.string().optional(),
    dietaryStyle: z.string().optional(),
    maxActiveMinutes: z.number().int().positive().optional(),
    limit: z.number().int().positive().max(50).default(10),
});
export const RecipeSearchResultSchema = z.object({
    recipeId: z.string(),
    titleDe: z.string(),
    titleEn: z.string(),
    dietaryTags: z.array(z.string()),
    allergenTags: z.array(z.string()),
    activeTimeMinutes: z.number().int().nonnegative(),
    totalTimeMinutes: z.number().int().nonnegative(),
    kcalPerServing: z.number().int().nonnegative(),
    sourceConfidence: z.number().min(0).max(1),
});
export const SearchRecipesOutputSchema = z.object({
    recipes: z.array(RecipeSearchResultSchema),
});
// ---------------------------------------------------------------------------
// compose_recipe — Amy composes a fresh recipe as canonical ingredient-ID
// structure only (ADR-013 Decision 1: "Amy composes recipes freely ... but
// every composed recipe must be expressed as canonical ingredient IDs from
// the ingredient registry plus quantities. The LLM never supplies
// authoritative nutrition or allergen values — it supplies structure.").
//
// The deterministic validator (T06) revalidates composition expansion,
// allergen intersection, nutrition tolerance, and time on every composed
// recipe before it can enter a plan — this tool's output is a proposal, not
// a verified recipe. To make that boundary structural rather than a
// convention callers can drift from, ComposeRecipeOutputSchema is a `.strict()`
// object tree: parsing rejects any object carrying nutrition/kcal/allergen
// keys (or any other unrecognized key) rather than silently stripping them.
// See tests/compose-recipe-no-nutrition.test.ts.
// ---------------------------------------------------------------------------
/** ADR-023: snack slots are composable too — a thin snack pool falls back to the same compose path as main meals. */
export const ComposeRecipeSlotSchema = z.enum([
    "breakfast",
    "morning_snack",
    "lunch",
    "afternoon_snack",
    "dinner",
    "snack",
]);
export const ComposeRecipeConstraintsSummarySchema = z
    .object({
    dietaryClasses: z.array(z.string()),
    excludedIngredientIds: z.array(z.string()),
    excludedAllergens: z.array(z.string()),
})
    .strict();
export const ComposeRecipeInputSchema = z
    .object({
    slot: ComposeRecipeSlotSchema,
    constraintsSummary: ComposeRecipeConstraintsSummarySchema,
    /** Free-form hints (cuisine, ingredients to use up, mood) — never authoritative. */
    preferences: z.array(z.string()),
    /** Advisory only — the validator's nutrition tolerance check is authoritative. */
    targetKcalPerServing: z.number().positive(),
    locale: LocaleSchema,
})
    .strict();
const ComposeRecipeLocalizedTextSchema = z.object({ de: z.string(), en: z.string() }).strict();
const ComposeRecipeLocalizedStepsSchema = z
    .object({ de: z.array(z.string()), en: z.array(z.string()) })
    .strict();
export const ComposeRecipeIngredientSchema = z
    .object({
    ingredientId: z.string(),
    quantity: z.number().positive(),
    unit: CanonicalUnitSchema,
})
    .strict();
export const ComposeRecipeOutputSchema = z
    .object({
    title: ComposeRecipeLocalizedTextSchema,
    description: ComposeRecipeLocalizedTextSchema,
    steps: ComposeRecipeLocalizedStepsSchema,
    ingredients: z.array(ComposeRecipeIngredientSchema),
    activeMinutes: z.number().int().nonnegative(),
    totalMinutes: z.number().int().nonnegative(),
    servingsDefault: z.number().int().min(1),
    skill: CookingSkillSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// propose_plan_change / apply_plan_change — wrap the plan proposal/apply Lambdas
// ---------------------------------------------------------------------------
export const ProposePlanChangeInputSchema = PlanProposalRequestSchema.extend({
    planId: z.string(),
});
export const ProposePlanChangeOutputSchema = PlanProposalResponseSchema;
export const ApplyPlanChangeInputSchema = PlanApplyRequestSchema.extend({
    planId: z.string(),
    proposalId: z.string(),
});
export const ApplyPlanChangeOutputSchema = PlanMutationResultSchema;
// ---------------------------------------------------------------------------
// extract_pantry_items / upsert_pantry_items
// ---------------------------------------------------------------------------
export const ExtractPantryItemsInputSchema = PantryExtractionRequestSchema;
export const ExtractPantryItemsOutputSchema = PantryExtractionResponseSchema;
export const UpsertPantryItemsInputSchema = PantryConfirmationRequestSchema;
export const UpsertPantryItemsOutputSchema = PantryResponseSchema;
// ---------------------------------------------------------------------------
// record_feedback
// ---------------------------------------------------------------------------
export const RecordFeedbackInputSchema = MealOutcomeRequestBaseSchema.extend({
    mealId: z.string(),
}).refine(partialRequiresActualServings, {
    message: "actualServings is required when outcome is 'partial'",
    path: ["actualServings"],
});
export const RecordFeedbackOutputSchema = MealOutcomeResponseSchema;
// ---------------------------------------------------------------------------
// propose_memory / confirm_memory
// ---------------------------------------------------------------------------
// Same wire shape as POST /v1/memory-candidates (endpoints.ts
// createMemoryCandidate) — single source of truth in memory.ts.
export const ProposeMemoryInputSchema = MemoryCandidateCreateRequestSchema;
export const ProposeMemoryOutputSchema = MemoryCandidateSchema;
export const ConfirmMemoryInputSchema = z.object({
    candidateId: z.string(),
    choice: MemoryChoiceSchema,
});
export const ConfirmMemoryOutputSchema = MemoryCandidateActionResponseSchema;
// ---------------------------------------------------------------------------
// get_savings / billing_entitlement — read-only, server-computed
// ---------------------------------------------------------------------------
// get_pantry (ADR-064, pre-beta program WS5): Amy's READ-ONLY view of the
// authoritative pantry — confirmed stock only (typed quick-adds, purchases
// confirmed as bought / already had on the Shop page, minus what logged
// meals used). Never writes; shopping-list leftovers stay forecasts.
import { PreparedBatchesResponseSchema } from "./preparedBatches.js";
export const GetPantryInputSchema = EmptyInputSchema;
export const GetPantryOutputSchema = PantryResponseSchema;
// get_prepared_food (ADR-065): Amy's READ-ONLY view of Prepared Food
// Inventory — batches the user CONFIRMED they cooked, with the portions
// left. Never inferred from the plan; never written by Amy.
export const GetPreparedFoodInputSchema = EmptyInputSchema;
export const GetPreparedFoodOutputSchema = PreparedBatchesResponseSchema;
export const GetSavingsInputSchema = EmptyInputSchema;
export const GetSavingsOutputSchema = InsightsResponseSchema;
// ADR-027 rules 2-3: consistency/trend/calibration — observable facts only,
// never a verdict (see amy_tools.py's get_progress docstring for the full
// usage rules Amy must follow when citing this).
export const GetProgressInputSchema = EmptyInputSchema;
export const GetProgressOutputSchema = ProgressSummaryResponseSchema;
export const BillingEntitlementInputSchema = EmptyInputSchema;
export const BillingEntitlementOutputSchema = EntitlementSummarySchema;
// ---------------------------------------------------------------------------
// get_user_context input (no args — identity is platform-propagated)
// ---------------------------------------------------------------------------
export const GetUserContextInputSchema = EmptyInputSchema;
export const GetUserContextOutputSchema = UserContextPackSchema;
export const tools = [
    {
        name: "get_user_context",
        description: "Assemble the minimum authorized per-turn context pack; no full transcript dump.",
        readOrWrite: "read",
        input: GetUserContextInputSchema,
        output: GetUserContextOutputSchema,
    },
    {
        name: "search_recipes",
        description: "Search the sourced recipe catalog; returns provenance and filters applied.",
        readOrWrite: "read",
        input: SearchRecipesInputSchema,
        output: SearchRecipesOutputSchema,
    },
    {
        name: "compose_recipe",
        description: "Amy composes a fresh recipe as canonical ingredient-ID structure only; never emits nutrition/allergen data — the deterministic validator revalidates before use (ADR-013 Decision 1).",
        readOrWrite: "read",
        input: ComposeRecipeInputSchema,
        output: ComposeRecipeOutputSchema,
    },
    {
        name: "propose_plan_change",
        description: "Wraps optimize+validate; returns a validated diff and confirmation token. Never persists.",
        readOrWrite: "proposal",
        input: ProposePlanChangeInputSchema,
        output: ProposePlanChangeOutputSchema,
    },
    {
        name: "apply_plan_change",
        description: "Apply a validated proposal atomically using its confirmation token, idempotency key, and base revision.",
        readOrWrite: "write",
        input: ApplyPlanChangeInputSchema,
        output: ApplyPlanChangeOutputSchema,
    },
    {
        name: "extract_pantry_items",
        description: "Structured pantry candidates with confidence; never writes the database.",
        readOrWrite: "read",
        input: ExtractPantryItemsInputSchema,
        output: ExtractPantryItemsOutputSchema,
    },
    {
        name: "upsert_pantry_items",
        description: "Persist confirmed candidate token and user-edited values.",
        readOrWrite: "write",
        input: UpsertPantryItemsInputSchema,
        output: UpsertPantryItemsOutputSchema,
    },
    {
        name: "get_prepared_food",
        description: "Read Prepared Food Inventory: batches the user confirmed they cooked and how many portions are left, plus batches the plan schedules that are NOT prepared yet. Never writes; a scheduled batch is not cooked food.",
        readOrWrite: "read",
        input: GetPreparedFoodInputSchema,
        output: GetPreparedFoodOutputSchema,
    },
    {
        name: "get_pantry",
        description: "Read the authoritative pantry: confirmed stock only (quick-adds, purchases confirmed on the Shop page, minus logged meals). Never writes; a shopping-list expected leftover is a forecast, not stock.",
        readOrWrite: "read",
        input: GetPantryInputSchema,
        output: GetPantryOutputSchema,
    },
    {
        name: "record_feedback",
        description: "Explicit meal outcome/feedback; may return a memory candidate.",
        readOrWrite: "write",
        input: RecordFeedbackInputSchema,
        output: RecordFeedbackOutputSchema,
    },
    {
        name: "propose_memory",
        description: "Propose a learnable preference with provenance, confidence, and effect; one-off vs durable choice.",
        readOrWrite: "proposal",
        input: ProposeMemoryInputSchema,
        output: ProposeMemoryOutputSchema,
    },
    {
        name: "confirm_memory",
        description: "Explicit confirmation of a memory candidate; hard constraints are excluded from this path.",
        readOrWrite: "write",
        input: ConfirmMemoryInputSchema,
        output: ConfirmMemoryOutputSchema,
    },
    {
        name: "get_savings",
        description: "Read the versioned savings/insights calculation service result; never LLM arithmetic.",
        readOrWrite: "read",
        input: GetSavingsInputSchema,
        output: GetSavingsOutputSchema,
    },
    {
        name: "get_progress",
        description: "Read the deterministic progress summary (consistency, 7-day weight trend, calibration state) — every field is an observed fact; trendDirection is null under the 14-day gate and must never be inferred by Amy herself.",
        readOrWrite: "read",
        input: GetProgressInputSchema,
        output: GetProgressOutputSchema,
    },
    {
        name: "billing_entitlement",
        description: "Server-side entitlement state only.",
        readOrWrite: "read",
        input: BillingEntitlementInputSchema,
        output: BillingEntitlementOutputSchema,
    },
];
export const getTool = (name) => {
    const found = tools.find((t) => t.name === name);
    if (!found)
        throw new Error(`Unknown tool: ${name}`);
    return found;
};
//# sourceMappingURL=tools.js.map